import { toast } from 'sonner';

import type { WorkflowView } from '@/lib/api/generated/model';
import { useWorkspace } from '@/features/workspaces';
import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';

import { deletionSummary } from '../deletionSummary';
import type { DeletableTask, DeletionSummary } from '../deletionSummary';
import { useDeleteWorkflow } from '../hooks/useDeleteWorkflow';
import { nameList } from '../nameList';

/**
 * Confirms a workflow delete before it happens.
 *
 * The one irreversible action in this product: the service removes the workflow
 * "and everything positioned on it". So the dialog counts what would go and
 * names the tasks, rather than asserting in prose that tasks exist — the numbers
 * all come from the workspace aggregate the list behind it is already rendering
 * from, so being specific costs no request.
 *
 * Closing is blocked while the delete is in flight, so the mutation always
 * resolves in this dialog rather than against an already-dismissed one.
 * Success closes it and says so; failure leaves it open with the error
 * inline so the author can retry without losing their place.
 */
export function DeleteWorkflowDialog({
  workspaceId,
  workflow,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  workflow: WorkflowView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const deleteMutation = useDeleteWorkflow(workspaceId);
  const { error, isPending } = deleteMutation;
  // The same query the list is drawn from, so this is a read of what is already
  // in the cache rather than a second trip.
  const workspace = useWorkspace(workspaceId);
  const tasks = (workspace.data?.tasks ?? []) as DeletableTask[];
  const summary = deletionSummary(workflow, tasks);

  const cancel = () => {
    if (isPending) return;
    onOpenChange(false);
    deleteMutation.reset();
  };

  const confirmDelete = () => {
    if (isPending) return;
    const { name } = workflow;
    // The promise rather than `mutate`'s callbacks: success refetches the
    // list this dialog's row menu lives in, so the row (and this dialog) can
    // unmount before a per-call `onSuccess` would run — and take the toast
    // with it.
    deleteMutation.mutateAsync(workflow.id).then(
      () => {
        toast.success(`Deleted ${name}.`);
        onOpenChange(false);
        deleteMutation.reset();
      },
      () => {
        // Deliberately empty: the refusal is rendered inline from the
        // mutation's own state (`error` below), and odd 409s are reported by
        // the hook. Handling it here only stops an unhandled rejection.
      },
    );
  };

  return (
    <ConfirmDestructiveDialog
      open={open}
      title={<>Delete {workflow.name}?</>}
      confirmLabel="Delete workflow"
      isPending={isPending}
      error={error?.message ?? null}
      onConfirm={confirmDelete}
      onCancel={cancel}
    >
      <Consequence summary={summary} /> {SPEND_STAYS} This cannot be undone.
    </ConfirmDestructiveDialog>
  );
}

/**
 * What a delete does not take (spec 146): usage records outlive the workflow, so its
 * spend stays where it was, under its last name.
 */
const SPEND_STAYS =
  'Its usage and spend stay on the Usage pages under its name.';

/**
 * What is about to be lost, in prose.
 *
 * Split out because the empty cases are the ones most likely to be written
 * carelessly, and they are the ones that matter most: a draft nobody has used is
 * harmless to delete, and "0 tasks" reads exactly like a count that failed.
 * Tasks are named and structure is only counted — a task is someone's actual
 * work, where a step is structure the author can see on the canvas behind this
 * dialog.
 */
function Consequence({ summary }: { summary: DeletionSummary }) {
  const { phases, steps, taskTitles } = summary;

  const structure =
    phases === 0 && steps === 0
      ? null
      : `${String(phases)} ${phases === 1 ? 'phase' : 'phases'} and ${String(steps)} ${steps === 1 ? 'step' : 'steps'}`;

  if (taskTitles.length === 0) {
    return (
      <>
        {structure === null
          ? 'Nothing has been built on it yet.'
          : `This removes ${structure}. No tasks are on it.`}
      </>
    );
  }

  const tasks = `${String(taskTitles.length)} ${taskTitles.length === 1 ? 'task' : 'tasks'}`;

  return (
    <>
      {structure === null
        ? `This removes ${tasks}: `
        : `This removes ${structure}, and ${tasks}: `}
      {nameList(taskTitles)}.
    </>
  );
}
